import React, { useEffect, useMemo, useState } from 'react';

import { formatStaffName } from '../../utils/staffName';
import { Z_DIALOG } from '../../constants/zLayers';
import { fetchAssignableStaff } from '../../services/api/patientPlanApi';

/**
 * ReassignDialog — pick a new assignee + supply a required reason.
 *
 * A2-S6 (department scoping): the assignee options come from the server-side
 * scoped endpoint GET …/sittings/{sitting_id}/assignable-staff, which already
 * returns ONLY staff who are both role-eligible for THIS procedure AND in the
 * procedure's department (or org-wide / no-dept). This dialog renders that list
 * verbatim — it does NOT re-filter or merge in the unscoped org roster (single
 * source of truth; the assign guard enforces the same rule server-side).
 *
 * Backend guards:
 *   - Reassignment to a different member_id requires reason >= 3 chars.
 *   - Same-assignee "re-confirmation" (used for toggling requires_approval)
 *     skips the reason requirement — but Phase 1 doesn't surface that path
 *     here; the dialog always enforces reason when the assignee changes.
 *
 * Props:
 *   sitting     — the sitting being reassigned (for display + currentAssignee;
 *                 must carry plan_id + sitting_id for the scoped fetch)
 *   viewer      — { memberId, role }
 *   onSubmit    — async ({ assigned_to_member_id, reason }) => void
 *   onCancel    — () => void
 */

const ROLE_LABEL = {
  DOCTOR: 'Doctor',
  NURSE: 'Nurse',
};

/**
 * Pure option builder — turns the scoped `staff` response into <option> rows.
 * Extracted so the label/self/dedupe logic is unit-testable without mounting.
 *
 * @param {Array<{member_id, name, role, specialization?}>} staff
 *   already role+dept-scoped staff from the assignable-staff endpoint
 * @param {{memberId?: string, role?: string}} viewer
 * @returns {Array<{member_id, label, role}>}
 */
export function buildReassignOptions(staff = [], viewer = {}) {
  const list = [];
  const seen = new Set();

  // "Myself" is only offered when the viewer is themselves in the scoped
  // list — offering self unconditionally would let a viewer self-assign
  // out-of-scope, contradicting the server rule.
  const self = viewer?.memberId
    ? staff.find((m) => m.member_id === viewer.memberId)
    : null;
  if (self) {
    list.push({
      member_id: self.member_id,
      label: 'Myself',
      role: (self.role || viewer.role || '').toUpperCase(),
    });
    seen.add(self.member_id);
  }

  for (const m of staff) {
    if (!m?.member_id || seen.has(m.member_id)) continue;
    seen.add(m.member_id);
    const role = (m.role || '').toUpperCase();
    const name = m.name || formatStaffName(m) || m.member_id;
    let label;
    if (role === 'DOCTOR') {
      const spec = (m.specialization || '').trim();
      label = spec ? `Dr. ${name} · ${spec}` : `Dr. ${name}`;
    } else if (role === 'NURSE') {
      label = `Nurse · ${name}`;
    } else {
      const roleLbl = ROLE_LABEL[role] || role;
      label = roleLbl ? `${name} · ${roleLbl}` : name;
    }
    list.push({ member_id: m.member_id, label, role });
  }
  return list;
}

export default function ReassignDialog({ sitting, viewer, onSubmit, onCancel }) {
  const currentAssignee =
    sitting.assigned_to_member_id
    || sitting.assigned_to?.member_id
    || '';
  // Phase 1c — prefer the denormalized display name over the raw id.
  const currentAssigneeName =
    sitting.assigned_to_name
    || sitting.assigned_to?.name
    || '';

  // A2-S6 — dept-scoped, role-eligible staff for THIS sitting. Fetched on
  // open; rendered verbatim (no client-side filter).
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const planId = sitting?.plan_id;
    const sittingId = sitting?.sitting_id;
    if (!planId || !sittingId) {
      setLoading(false);
      setLoadError('Missing plan or sitting reference.');
      return undefined;
    }
    setLoading(true);
    setLoadError('');
    (async () => {
      const r = await fetchAssignableStaff(planId, sittingId);
      if (cancelled) return;
      if (r?.success) {
        setStaff(Array.isArray(r.data?.staff) ? r.data.staff : []);
      } else {
        setStaff([]);
        setLoadError(r?.error || 'Failed to load assignable staff.');
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [sitting?.plan_id, sitting?.sitting_id]);

  const options = useMemo(
    () => buildReassignOptions(staff, viewer),
    [staff, viewer],
  );

  const isEmpty = !loading && !loadError && options.length === 0;

  const [assignee, setAssignee] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Once options load, default to the first member who isn't the current
  // assignee (a reassign should move the work elsewhere).
  useEffect(() => {
    if (loading) return;
    const preferred = options.find((o) => o.member_id !== currentAssignee)?.member_id
      || options[0]?.member_id
      || '';
    setAssignee(preferred);
  }, [loading, options, currentAssignee]);

  const isSameAssignee = assignee === currentAssignee;
  const reasonValid = isSameAssignee ? true : reason.trim().length >= 3;
  const canSubmit = !!assignee && reasonValid && !isEmpty;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onSubmit({
        assigned_to_member_id: assignee,
        reason: isSameAssignee ? null : reason.trim(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      // z-50 was hidden behind the chart pane (Z_CHART_PANE=900); Z_DIALOG
      // (1300) keeps the modal above the chart/widget rail. Same fix as PromptDialog.
      // role="dialog" marks this as a keep-zone so RoleTaskBadge doesn't
      // collapse (and unmount this modal) when the user clicks inside it.
      role="dialog"
      aria-modal="true"
      style={{ zIndex: Z_DIALOG }}
      className="fixed inset-0 flex items-center justify-center bg-black/40 p-4"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5"
      >
        <h2 className="text-lg font-semibold mb-3">Reassign sitting</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
          Currently assigned to {currentAssigneeName || currentAssignee || '—'}.
        </p>

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">New assignee *</label>

          {loading && (
            <p className="text-sm text-slate-500 dark:text-slate-400 py-2">
              Loading eligible staff…
            </p>
          )}

          {!loading && loadError && (
            <p className="text-sm text-rose-600 dark:text-rose-300 py-2">
              {loadError}
            </p>
          )}

          {isEmpty && (
            <div
              className="rounded-md border border-amber-400 dark:border-amber-600
                         bg-amber-50 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100
                         px-3 py-2 text-sm"
            >
              No eligible staff in this department for this procedure.
            </div>
          )}

          {!loading && !loadError && !isEmpty && (
            <select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              required
            >
              <option value="">— choose —</option>
              {options.map((o) => (
                <option key={o.member_id} value={o.member_id}>
                  {o.label}
                  {o.member_id === currentAssignee ? ' (current)' : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {!isSameAssignee && !isEmpty && !loading && !loadError && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Reason (min 3 chars) *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              required
              minLength={3}
            />
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !canSubmit}
            className="px-4 py-1.5 text-sm rounded-md bg-teal-600 hover:bg-teal-700 text-white disabled:bg-teal-300 disabled:opacity-60"
          >
            {busy ? 'Reassigning…' : 'Reassign'}
          </button>
        </div>
      </form>
    </div>
  );
}
